import { Component, ReactNode } from 'react'
import { ModalNodeProps } from '../../../../components/modal.component'
import { preventMouseEvent } from '../../../../../utils'
import {
  AddressType,
  AddressTypeLabel,
  CustomerAddressDto,
  ResidentialType,
  TempCustomerAddressDto
} from '../../../../../definition'
import { SWITCH_FUNCTION } from '../../../../../constants'
import { asyncCreateCustomerAddress, asyncUpdateCustomerAddress } from '../../../../../services'
import { toast } from 'react-toastify'
import Switch from 'react-switch'
import { SwitchColor } from '../../../../../constants/style.config'
import { ADDRESS_MANDATORY, CONFIG_RESIDENTIAL_TYPE } from '../../../../../constants/common.config'
import { requiredIcon } from '../../../../components/requiredIcon'
import { InputBase, SelectBase } from '../../../../../components'
import { extractAddressDetails, GoogleMapInput } from '../../../../../components/google-map-input'

export interface AddressDetailProps {
  isNew: boolean
  address?: CustomerAddressDto
  addressType?: AddressType
  accountId?: string
  refreshData?: () => void
  isCreatingCustomer?: boolean
  getTemporaryAddress?: (address: TempCustomerAddressDto) => void
  temporaryAddress?: TempCustomerAddressDto | null
  setSelectAddress?: (address: CustomerAddressDto, addressType: AddressType, index?: number) => void
  isFirstAddress?: boolean
}

export class AddressDetail extends Component<
  ModalNodeProps<AddressDetailProps>,
  {
    addressName: string
    addressNameError?: boolean
    addressLine1: string
    lng?: number
    lat?: number
    addressLine1Error?: boolean
    addressLine2: string
    addressLine2Error?: boolean
    unitNo?: string
    addressPostalCode: string
    addressPostalCodeError?: boolean
    addressCity?: string
    addressCityError?: boolean
    addressCountry?: string
    addressCountryError?: boolean
    addressResidentialType: ResidentialType
    addressResidentialTypeError: boolean
    isPrimary: boolean
  }
> {
  componentDidMount() {
    if (this.props.input?.address && this.props.input?.address?.accountId) {
      const address = this.props.input.address
      this.setState({
        addressName: address.addressName as string,
        addressLine1: address.addressLine1,
        lng: address.lng,
        lat: address.lat,
        addressLine2: address.addressLine2,
        unitNo: address.unitNo,
        addressPostalCode: address.addressPostalCode,
        addressCity: address.addressCity,
        addressCountry: address.addressCountry,
        addressResidentialType: address.residentialType as ResidentialType,
        isPrimary: address.isPrimary as boolean
      })
    }
    if (this.props.input?.isCreatingCustomer || this.props.input?.isFirstAddress) {
      this.setState({
        isPrimary: true
      })
    }
    if (this.props.input?.temporaryAddress) {
      const temporaryAddress = this.props.input?.temporaryAddress
      this.setState({
        addressName: temporaryAddress.addressName,
        addressLine1: temporaryAddress.addressLine1,
        addressLine2: temporaryAddress.addressLine2,
        unitNo: temporaryAddress.unitNo,
        lng: temporaryAddress.lng,
        lat: temporaryAddress.lat,
        addressPostalCode: temporaryAddress.addressPostalCode,
        addressCity: temporaryAddress.addressCity,
        addressCountry: temporaryAddress.addressCountry,
        addressResidentialType: temporaryAddress.residentialType,
        isPrimary: temporaryAddress.isPrimary
      })
    }
  }

  onSubmit() {
    if (this.props.input?.address?.id) {
      this.onUpdate()
    } else {
      if (this.props.input?.isCreatingCustomer) {
        this.saveAddressForCreatingCustomer()
      } else {
        this.onCreate()
      }
    }
  }

  validateAddress() {
    const state = { ...this.state }
    const messageError = []

    if (!state.addressName) {
      state.addressNameError = true
      messageError.push(<p>Address Name is not requied!</p>)
    }
    if (!state.addressLine1 || !state.lng || !state.lat) {
      state.addressLine1Error = true
      messageError.push(<p>Address is requied!</p>)
    }
    if (!state.addressPostalCode) {
      state.addressPostalCodeError = true
      messageError.push(<p>Postal Code is requied!</p>)
    }
    if (!state.addressResidentialType) {
      state.addressResidentialTypeError = true
      messageError.push(<p>Residential Type is requied!</p>)
    }

    this.setState(state)

    return {
      isValid: messageError.length === 0,
      stateAfterValid: {
        addressName: state.addressName,
        addressLine1: state.addressLine1,
        addressLine2: state.addressLine2,
        unitNo: state.unitNo,
        addressPostalCode: state.addressPostalCode,
        addressCountry: state.addressCountry,
        addressCity: state.addressCity,
        addressResidentialType: state.addressResidentialType,
        isPrimary: state.isPrimary,
        lng: state.lng,
        lat: state.lat
      }
    }
  }

  async onUpdate() {
    const accountId = this.props.input?.accountId
    const addressType = this.props.input?.addressType
    const addressId = this.props.input?.address?.id

    if (!accountId || !addressType || !addressId) {
      return
    }

    const { isValid, stateAfterValid } = this.validateAddress()
    if (!isValid) {
      return
    }

    const updateAddressJSON = {
      accountId: accountId,
      addressType: addressType,
      residentialType: stateAfterValid.addressResidentialType,
      ...stateAfterValid
    }

    try {
      const data = await asyncUpdateCustomerAddress(updateAddressJSON, addressId)
      toast.success('Success updated!')
      this.props.onDone?.(data)
      this.props.input?.refreshData?.()
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || error.message
      toast.error(`${errorMessage}`)
    }
  }

  async onCreate() {
    const accountId = this.props.input?.accountId
    const addressType = this.props.input?.addressType

    if (!accountId || !addressType) {
      return
    }

    const { isValid, stateAfterValid } = this.validateAddress()
    if (!isValid) {
      return
    }

    const createAddressJSON = {
      accountId: accountId,
      addressType: addressType,
      residentialType: stateAfterValid.addressResidentialType,
      ...stateAfterValid
    }

    try {
      await asyncCreateCustomerAddress(createAddressJSON)
      toast.success('Success created!')
      this.props.onDone?.()
      this.props.input?.refreshData?.()
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || error.message
      toast.error(`${errorMessage}`)
    }
  }

  saveAddressForCreatingCustomer() {
    const addressType = this.props.input?.addressType

    if (!addressType) {
      return
    }

    const { isValid, stateAfterValid } = this.validateAddress()
    if (!isValid) {
      return
    }

    this.props.input?.getTemporaryAddress?.({
      ...stateAfterValid,
      addressType,
      residentialType: stateAfterValid.addressResidentialType
    })

    this.props.onDone?.()
  }

  handleAuto = (place: google.maps.places.PlaceResult) => {
    const { city, country, postal_code } = extractAddressDetails(place)
    if (place.formatted_address) {
      this.setState({
        addressLine1: place.formatted_address,
        lat: place.geometry?.location?.lat(),
        lng: place.geometry?.location?.lng(),
        addressLine1Error: false,
        addressCity: city || undefined,
        addressCountry: country || undefined,
        addressPostalCode: postal_code || ''
      })
    }
  }

  render(): ReactNode {
    const state = this.state || {}
    return (
      <div className='customer-address-detail-modal'>
        <form onSubmit={this.onSubmit.bind(this)}>
          <div>
            <label>Address Name {requiredIcon(ADDRESS_MANDATORY.name)}</label>
            <InputBase
              placeholder='Address Name (Home/Office)'
              value={state.addressName}
              error={state.addressNameError}
              onChange={(e) =>
                this.setState({
                  addressName: e.target.value,
                  addressNameError: false
                })
              }
            />
          </div>
          <div>
            <label>Address {requiredIcon(ADDRESS_MANDATORY.line1)}</label>
            <GoogleMapInput
              className={state.addressLine1Error ? 'input-error' : ''}
              defaultValue={state.addressLine1}
              onSelect={this.handleAuto}
              placeholder={'Address'}
            />
          </div>
          <div>
            <label>Unit No. {requiredIcon(ADDRESS_MANDATORY.line2)}</label>
            <InputBase
              placeholder='Unit No.'
              value={state.unitNo}
              onChange={(e) =>
                this.setState({
                  unitNo: e.target.value
                })
              }
            />
          </div>
          <div>
            <label>Postal Code {requiredIcon(ADDRESS_MANDATORY.postalCode)}</label>
            <InputBase
              placeholder='Postal Code'
              value={state.addressPostalCode}
              error={state.addressPostalCodeError}
              onChange={(e) =>
                this.setState({
                  addressPostalCode: e.target.value,
                  addressPostalCodeError: false
                })
              }
            />
          </div>
          <div>
            <label>Country {requiredIcon(ADDRESS_MANDATORY.country)}</label>
            <InputBase
              placeholder='Country (Optional)'
              value={state.addressCountry}
              error={state.addressCountryError}
              onChange={(e) =>
                this.setState({
                  addressCountry: e.target.value,
                  addressCountryError: false
                })
              }
            />
          </div>
          <div>
            <label>City {requiredIcon(ADDRESS_MANDATORY.city)}</label>
            <InputBase
              placeholder='City (Optional)'
              value={state.addressCity}
              error={state.addressCityError}
              onChange={(e) =>
                this.setState({
                  addressCity: e.target.value,
                  addressCityError: false
                })
              }
            />
          </div>
          {SWITCH_FUNCTION.residential_type && (
            <div>
              <label>Residential Type {requiredIcon(ADDRESS_MANDATORY.residentialType)}</label>
              <SelectBase
                value={state.addressResidentialType}
                error={state.addressResidentialTypeError}
                options={[
                  ...Object.entries(CONFIG_RESIDENTIAL_TYPE)
                    .filter(([_, config]) => config.show)
                    .map(([key, config]) => ({
                      value: key,
                      label: config.label
                    }))
                ]}
                onChange={(value) => {
                  this.setState({
                    ...this.state,
                    addressResidentialType: value as ResidentialType,
                    addressResidentialTypeError: !value
                  })
                }}
                className='select'
                placeholder='Select Residential Type'
              />
            </div>
          )}
          <div className='customer-address-detail-modal-setdefault'>
            Set as primary {AddressTypeLabel[this.props.input?.addressType || AddressType.Designated]} address
            <Switch
              onColor={SwitchColor}
              uncheckedIcon={false}
              checkedIcon={false}
              onChange={(checked) => {
                this.setState({
                  isPrimary: checked
                })
              }}
              checked={!!state.isPrimary}
              disabled={this.props.input?.isCreatingCustomer}
            />
          </div>
          <button className='customer-address-detail-modal-btn' onClick={preventMouseEvent(this.onSubmit.bind(this))}>
            {this.props.input?.isNew ? 'Create' : 'Save'}
          </button>
        </form>
      </div>
    )
  }
}
